import { inject, Injectable } from '@angular/core';
import {
	collectionCount,
	FIRESTORE,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { OrganizationConfigService } from '@context/frontend/organization';
import { PaginatedPageItem } from '@context/frontend/pagination';
import { ChemicalUsageLog } from '@context/shared/types/chemical';
import { getCutoffDate } from '@context/shared/types/common';
import {
	collection,
	CollectionReference,
	DocumentSnapshot,
	getDocs,
	limit,
	onSnapshot,
	orderBy,
	query,
	startAfter,
	where,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

export interface GuestUsageLogsPage {
	items: PaginatedPageItem<ChemicalUsageLog>[];
	lastDoc: DocumentSnapshot<ChemicalUsageLog> | null;
	totalElements: number;
}

export type GuestStreamUsageLogsCallback = (page: GuestUsageLogsPage) => void;

@Injectable({ providedIn: 'root' })
export class GuestChemicalUsageLogService {
	static readonly CollectionId = 'usageLogs';

	private readonly firestore = inject(FIRESTORE);
	private readonly organizationId = inject(ORGANIZATION_ID);
	private readonly organizationConfigService = inject(
		OrganizationConfigService,
	);

	private getUsageLogsCollection(
		organizationId: string,
	): CollectionReference<ChemicalUsageLog> {
		return collection(
			this.firestore,
			'organizations',
			organizationId,
			GuestChemicalUsageLogService.CollectionId,
		) as CollectionReference<ChemicalUsageLog>;
	}

	/**
	 * Subscribes to usage logs for an organization in real time.
	 * Only returns logs within the configured usageLogAliveDays window.
	 * @returns Unsubscribe function, or undefined if org id is missing.
	 */
	streamUsageLogs(
		pageSize: number,
		callback: GuestStreamUsageLogsCallback,
	): (() => void) | undefined {
		const orgId = this.organizationId.value;
		if (!orgId) {
			console.error(
				'GuestChemicalUsageLogService.streamUsageLogs: Organization ID required',
			);
			return undefined;
		}

		let unsub: (() => void) | null = null;
		let cancelled = false;

		this.organizationConfigService
			.getUsageLogAliveDays()
			.then((aliveDays) => {
				if (cancelled) return;
				const cutoffDate = getCutoffDate(aliveDays);

				const q = query(
					this.getUsageLogsCollection(orgId),
					where('deletedAt', '==', null),
					where('appliedAt', '>=', cutoffDate),
					orderBy('appliedAt', 'desc'),
				);

				unsub = onSnapshot(
					query(q, limit(pageSize)),
					async (snapshot) => {
						let lastDoc: DocumentSnapshot<ChemicalUsageLog> | null =
							null;
						const items = snapshot.docs.map((d) => {
							lastDoc = d;
							const data = d.data() as ChemicalUsageLog;
							return { data, ref: d.ref, id: d.id };
						});

						const totalElements = await firstValueFrom(
							collectionCount(q),
						);
						callback({ items, lastDoc, totalElements });
					},
					(error) => {
						console.error(
							'GuestChemicalUsageLogService.streamUsageLogs:',
							error,
						);
					},
				);
			});

		return () => {
			cancelled = true;
			unsub?.();
		};
	}

	/**
	 * Fetches the next page of usage logs after the given document.
	 * Only returns logs within the configured usageLogAliveDays window.
	 */
	async fetchNextPage(
		pageSize: number,
		startAfterDoc: DocumentSnapshot<ChemicalUsageLog> | null,
		callback: GuestStreamUsageLogsCallback,
	) {
		const orgId = this.organizationId.value;
		if (!orgId) {
			callback({
				items: [],
				lastDoc: null,
				totalElements: 0,
			});
			return;
		}

		const aliveDays =
			await this.organizationConfigService.getUsageLogAliveDays();
		const cutoffDate = getCutoffDate(aliveDays);

		let q = query(
			this.getUsageLogsCollection(orgId),
			where('deletedAt', '==', null),
			where('appliedAt', '>=', cutoffDate),
			orderBy('appliedAt', 'desc'),
		);

		const totalElements = await firstValueFrom(collectionCount(q));

		if (startAfterDoc) {
			q = query(q, startAfter(startAfterDoc));
		}

		return getDocs(query(q, limit(pageSize))).then((snapshot) => {
			let lastDoc: DocumentSnapshot<ChemicalUsageLog> | null = null;
			const items = snapshot.docs.map((d) => {
				lastDoc = d;
				const data = d.data() as ChemicalUsageLog;
				return { data, ref: d.ref, id: d.id };
			});
			callback({ items, lastDoc, totalElements });
		});
	}
}
